import { useState } from 'react';
import { ClipboardList, Plus } from 'lucide-react';
import { P } from '@stockpilot/shared';
import { useAppSelector } from '@/app/hooks';
import { useFormat } from '@/hooks/useAccess';
import {
  useCategoriesQuery, useCreateStockCountMutation, useStockCountsQuery,
  type StockCount, type StockCountStatus,
} from '@/api/endpoints';
import { apiErrorMessage } from '@/api/baseApi';
import { formatDate, formatMoney } from '@/lib/format';
import {
  Alert, Badge, Button, Card, EmptyState, ErrorState, Input, LoadingState, Modal,
  PageHeader, Pagination, Select, Table, Td, Th,
} from '@/components/ui';
import { Can } from '@/components/Can';
import { StockCountDetailModal } from './StockCountDetailModal';

export const COUNT_TONE: Record<StockCountStatus, 'neutral' | 'brand' | 'success' | 'warning'> = {
  DRAFT: 'neutral',
  IN_PROGRESS: 'brand',
  PENDING_APPROVAL: 'warning',
  APPROVED: 'success',
  CANCELLED: 'neutral',
};

const COUNT_LABEL: Record<StockCountStatus, string> = {
  DRAFT: 'draft',
  IN_PROGRESS: 'counting',
  PENDING_APPROVAL: 'awaiting approval',
  APPROVED: 'approved',
  CANCELLED: 'cancelled',
};

export function StockCountsPage() {
  const format = useFormat();
  const warehouses = useAppSelector((state) => state.tenant.context?.warehouses ?? []);

  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const { data, isLoading, isError, error, refetch, isFetching } = useStockCountsQuery({
    page,
    limit: 25,
    status: status || undefined,
  });
  const { data: categories } = useCategoriesQuery({ limit: 200 });
  const [createCount, createState] = useCreateStockCountMutation();

  const [warehouseId, setWarehouseId] = useState('');
  const [scope, setScope] = useState<'FULL' | 'CATEGORY'>('FULL');
  const [categoryId, setCategoryId] = useState('');
  const [notes, setNotes] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const submit = async (): Promise<void> => {
    setFormError(null);
    try {
      const created = await createCount({
        warehouseId,
        scope,
        categoryId: scope === 'CATEGORY' ? categoryId : undefined,
        notes: notes || undefined,
      }).unwrap();

      setCreating(false);
      setWarehouseId('');
      setScope('FULL');
      setCategoryId('');
      setNotes('');
      setOpenId(created.id);
    } catch (failure) {
      // The API refuses when nothing matches the scope.
      setFormError(apiErrorMessage(failure, 'That count could not be started'));
    }
  };

  return (
    <>
      <PageHeader
        title="Stock counts"
        description="Count what is physically there. Variances reach the ledger only on approval."
        action={
          <Can permission={P.STOCK_COUNT}>
            <Button icon={Plus} onClick={() => setCreating(true)} disabled={warehouses.length === 0}>
              Start a count
            </Button>
          </Can>
        }
      />

      <Card>
        <div className="flex flex-wrap items-end gap-3 border-b border-subtle p-4">
          <div className="w-full sm:w-56">
            <Select
              label="Status"
              value={status}
              onChange={(event) => {
                setStatus(event.target.value);
                setPage(1);
              }}
            >
              <option value="">All statuses</option>
              <option value="IN_PROGRESS">Counting</option>
              <option value="PENDING_APPROVAL">Awaiting approval</option>
              <option value="APPROVED">Approved</option>
              <option value="CANCELLED">Cancelled</option>
            </Select>
          </div>
        </div>

        {isLoading ? (
          <LoadingState />
        ) : isError ? (
          <ErrorState message={apiErrorMessage(error)} onRetry={() => void refetch()} />
        ) : data && data.items.length === 0 ? (
          <EmptyState
            icon={ClipboardList}
            title={status ? 'Nothing with that status' : 'No stock counts yet'}
            description={
              status
                ? 'Clear the filter to see every count.'
                : 'Start a count to check the books against the shelves.'
            }
          />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>Number</Th>
                  <Th>Warehouse</Th>
                  <Th>Scope</Th>
                  <Th numeric>Lines</Th>
                  <Th numeric>Variance value</Th>
                  <Th>Started</Th>
                  <Th>Status</Th>
                </tr>
              </thead>
              <tbody className={isFetching ? 'opacity-60' : undefined}>
                {data?.items.map((count: StockCount) => (
                  <tr key={count.id} className="hover:bg-sunken">
                    <Td>
                      <button
                        type="button"
                        className="font-medium hover:underline"
                        onClick={() => setOpenId(count.id)}
                      >
                        {count.number}
                      </button>
                    </Td>
                    <Td className="text-secondary">{count.warehouse.name}</Td>
                    <Td className="text-secondary">{count.scope.toLowerCase()}</Td>
                    <Td numeric className="text-secondary">{count._count?.items ?? 0}</Td>
                    <Td
                      numeric
                      className={
                        Number(count.totalVarianceValue) === 0 ? 'text-secondary' : 'text-red-600'
                      }
                    >
                      {formatMoney(count.totalVarianceValue, format)}
                    </Td>
                    <Td className="text-secondary">
                      {count.startedAt ? formatDate(count.startedAt, format) : '—'}
                    </Td>
                    <Td>
                      <Badge tone={COUNT_TONE[count.status]}>{COUNT_LABEL[count.status]}</Badge>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>

            {data && (
              <Pagination
                page={data.meta.page}
                totalPages={data.meta.totalPages}
                total={data.meta.total}
                onPageChange={setPage}
              />
            )}
          </>
        )}
      </Card>

      <Modal
        open={creating}
        onClose={() => setCreating(false)}
        title="Start a stock count"
        description="The sheet is frozen now: each line records the system balance at this moment."
        footer={
          <>
            <Button variant="outline" onClick={() => setCreating(false)}>Cancel</Button>
            <Button
              onClick={() => void submit()}
              loading={createState.isLoading}
              disabled={!warehouseId || (scope === 'CATEGORY' && !categoryId)}
            >
              Start counting
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {formError && <Alert tone="danger">{formError}</Alert>}

          <Select
            label="Warehouse"
            required
            value={warehouseId}
            onChange={(event) => setWarehouseId(event.target.value)}
          >
            <option value="">Select a warehouse</option>
            {warehouses.map((warehouse) => (
              <option key={warehouse.id} value={warehouse.id}>{warehouse.name}</option>
            ))}
          </Select>

          <Select
            label="Scope"
            value={scope}
            onChange={(event) => setScope(event.target.value as 'FULL' | 'CATEGORY')}
            hint="A full count covers everything the warehouse holds."
          >
            <option value="FULL">Everything in the warehouse</option>
            <option value="CATEGORY">One category only</option>
          </Select>

          {scope === 'CATEGORY' && (
            <Select
              label="Category"
              required
              value={categoryId}
              onChange={(event) => setCategoryId(event.target.value)}
            >
              <option value="">Select a category</option>
              {categories?.items.map((category) => (
                <option key={category.id} value={category.id}>{category.name}</option>
              ))}
            </Select>
          )}

          <Input label="Notes" value={notes} onChange={(event) => setNotes(event.target.value)} />
        </div>
      </Modal>

      {openId && <StockCountDetailModal countId={openId} onClose={() => setOpenId(null)} />}
    </>
  );
}
